import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Options } from '@sb-shared/models/UI/filter';
import { Option } from './../../models/UI/filter';

@Component({
    selector: 'sb-multiselect-dropdown',
    templateUrl: './multiselect-dropdown.component.html',
    styleUrls: ['./multiselect-dropdown.component.scss'],
    standalone: false
})
export class MultiselectDropdownComponent implements OnInit {

  constructor(private translate: TranslateService) { }

  @Input() model: any;
  @Output() modelChange = new EventEmitter<any>();
  @Input() data: Options;
  @Input() singleSelection: boolean;
  @Input() idProp = 'id';
  @Input() labelProp = 'name';
  @Input() allLabel: string;
  @Input() placeholder: string;
  @Input() settings: any;
  // Convert between array of options and single or array of values
  @Input() reformat: boolean;
  @Input() disabled: boolean;
  @Input() doNotTranslateOptions: boolean;
  // Group options by this property — injects disabled "header" rows between groups (legacy `group-by` parity).
  @Input() groupBy: string;
  // List of group values that should expose a "Select all: <label>" action row inside the dropdown (legacy `selectByGroups` parity).
  @Input() selectByGroups: any[];
  // Opt-in to the legacy angularjs-dropdown-multiselect look without grouping: render explicit
  // "Check All" + "Uncheck All" action rows (with ✓ / ✗ icons) at the top of the list instead of
  // the underlying library's single "Select All" toggle. (groupBy already injects these rows.)
  @Input() enableCheckAllActions: boolean;
  // Translates a group value to its label (legacy `groupByTextProvider` parity).
  @Input() groupByTextProvider: (value: any) => string;
  // When set, the button collapses the selected chips into a "<n> <suffix>" count summary
  // instead of listing every chosen option (legacy `dynamicTitle`/`dynamicButtonTextSuffix`).
  @Input() showSelectedCount: boolean;
  // Word shown after the count (e.g. "42 checked"). Used verbatim — the caller supplies the
  // already-localized word. Defaults to the translated 'SB_X_items_selected'. The legacy
  // dropdown hard-coded the English "checked" here, so consumers wanting that pass it explicitly.
  @Input() selectedCountSuffix: string;
  computedSettings: any;
  // Data passed to the underlying library; equal to `data` unless `groupBy`/`selectByGroups` are set, in which case header + action rows are injected.
  displayData: Options;
  isOpened = false;
  @Output() onSelect = new EventEmitter<any>();
  @Output() onSelectAll = new EventEmitter<any>();
  @Output() onDeselect = new EventEmitter<any>();
  @Output() onDeselectAll = new EventEmitter<any>();
  @Output() onFilterChange = new EventEmitter<any>();
  @Output() onDropDownClose = new EventEmitter<any>();
  @Output() onClick = new EventEmitter<any>();
  @ViewChild('multiSelect', { read: ElementRef }) multiSelect: ElementRef;

  // Drives the `.sb-multiselect-grouped` host class that scopes the styled action-row CSS
  // (✓ Check All / ✗ Uncheck All + divider). Those rows are injected for groupBy OR the
  // standalone [enableCheckAllActions] opt-in, so both need the class — otherwise the rows
  // render as unstyled plain text.
  @HostBinding('class.sb-multiselect-grouped') get isGrouped(): boolean {
    return !!this.groupBy || !!this.enableCheckAllActions;
  }

  // Marker key on injected group-header rows; checked to distinguish them from regular options.
  private readonly groupHeaderFlag = '__sbGroupHeader__';
  // Marker key on injected "Select all: X" action rows; payload is the group value.
  private readonly selectAllActionFlag = '__sbSelectAllAction__';
  // Marker keys on the injected legacy Check All / Uncheck All rows.
  private readonly checkAllActionFlag = '__sbCheckAllAction__';
  private readonly uncheckAllActionFlag = '__sbUncheckAllAction__';
  // Zero-width Left-to-Right Mark (U+200E) prefixed onto every action-row label so we can
  // identify them as a group from a click event (text starts with this) and from CSS
  // (`[aria-label^="\200E"]`). The select-all-by-group rows use this alone.
  private readonly selectAllActionMarker = '‎';
  // Check All rows further append U+200B (ZWS); Uncheck All rows append U+200C (ZWNJ). Both
  // are zero-width / invisible to users but let CSS distinguish the two via attribute selectors
  // — that's how we render the brand-primary ✓ / ✗ icons next to each label without putting
  // visible chars inside the label string.
  private readonly checkAllMarker = '‎​';
  private readonly uncheckAllMarker = '‎‌';

  ngOnInit() {
    this.updateAllSelectedLabel();
    this.translate.onLangChange.subscribe(() => {
      this.updateAllSelectedLabel();
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.computedSettings = {
      // Sensible defaults — user can override any of these via [settings]
      enableCheckAll: true,
      allowSearchFilter: this.data?.length > 5,
      noFilteredDataAvailablePlaceholderText: this.translate.instant('SB_No_Results_Found'),
      // User-provided settings override the defaults above
      ...this.settings,
      // Canonical inputs always win — these mirror the AngularJS bindings
      singleSelection: this.singleSelection,
      idField: this.idProp,
      textField: this.labelProp,
      disabledField: 'isDisabled',
      selectAllText: this.translate.instant('SB_Select_All'),
      unSelectAllText: this.translate.instant('SB_UNSelect_All'),
      noDataAvailablePlaceholderText: this.translate.instant('SB_No_Results_Found'),
      searchPlaceholderText: this.translate.instant('SB_Search'),
      closeDropDownOnSelection: this.singleSelection,
      // When grouping is active (or a consumer opts in via [enableCheckAllActions]) we inject our
      // own legacy-style "Check All" + "Uncheck All" rows, so suppress the library's built-in
      // single-toggle row to avoid duplication.
      ...((this.groupBy || this.enableCheckAllActions) ? { enableCheckAll: false } : {})
    };
    if (changes.allLabel) {
      this.updateAllSelectedLabel();
    }
    if (changes.model?.currentValue != null && this.reformat) {
      if (this.singleSelection) {
        this.model = this.data.find(option => option.id === this.model);
      } else {
        this.model = this.data.filter(option => this.model.includes(option.id));
      }
    }
    const translateOptions = array => {
      if (!Array.isArray(array)) {
        return array;
      }
      array.forEach(item => {
        if (this.isInjectedRow(item)) {
          return;
        }
        item[this.labelProp] = this.doNotTranslateOptions ? item[this.labelProp] : this.translate.instant(item[this.labelProp]);
      });
      return array;
    }
    if (changes.data?.currentValue) {
      this.data = translateOptions(this.data);
    }
    if (changes.model?.currentValue != null) {
      this.model = translateOptions(this.model);
    }
    // Only rebuild displayData when an input that actually shapes it changes. Consumers often
    // bind [settings] to an inline object literal (a new ref every change-detection cycle), which
    // fires ngOnChanges constantly; rebuilding displayData each time would hand the underlying
    // <ng-multiselect-dropdown> a fresh [data] array on every CD. Its option *ngFor has no
    // trackBy, so a new array tears down and recreates every <li> — closing/flickering the open
    // dropdown the moment you (de)select an item. Keeping the reference stable avoids that.
    const displayDataInputsChanged =
      changes.data || changes.groupBy || changes.selectByGroups ||
      changes.idProp || changes.labelProp || changes.groupByTextProvider ||
      changes.doNotTranslateOptions || changes.enableCheckAllActions;
    if (displayDataInputsChanged || !this.displayData) {
      this.displayData = this.buildGroupedData(this.data);
    }
    // Parent-driven model/data updates (e.g. async first load) bypass change(), so refresh the
    // collapsed button label here too — otherwise the "All"/count summary renders stale or blank.
    if (changes.model || changes.data) {
      this.updateAllSelectedLabel();
    }
  }

  change(event: Options) {
    if (event === this.model) return;

    // Defensive: drop any injected pseudo-rows (group headers, Check/Uncheck All, "Select all: X")
    // the library might surface. Preserve array identity when nothing needs filtering, so the
    // existing reference-equality contract for callers passing flat data is maintained.
    const needsClean = Array.isArray(event) && event.some(item => this.isInjectedRow(item));
    const cleanEvent: any = needsClean
      ? event.filter(item => !this.isInjectedRow(item))
      : event;

    let newData;
    if (this.reformat && Array.isArray(cleanEvent)) {
      if (this.singleSelection) {
        newData = cleanEvent[0]?.id;
      } else {
        newData = cleanEvent.map(option => option.id);
      }
    } else {
      newData = cleanEvent;
      this.model = newData;
    }
    this.modelChange.emit(newData);
    this.updateAllSelectedLabel();
  }

  updateAllSelectedLabel() {
    setTimeout(() => {
      const dropdownButton = this.multiSelect?.nativeElement?.firstChild?.firstChild?.firstChild;
      if (!dropdownButton) {
        return;
      }
      // Count summary takes priority over the "All" affordance so a fully-selected list still
      // reads as a number (e.g. "42 selected") when the consumer opted into count display.
      if (this.showCountLabel()) {
        const suffix = this.selectedCountSuffix ?? this.translate.instant('SB_X_items_selected');
        dropdownButton.setAttribute('content', `${this.selectedCount()} ${suffix}`);
        return;
      }
      if (this.allSelected()) {
        dropdownButton.setAttribute('content', this.translate.instant(this.allLabel || 'SB_All'));
      }
    })
  }

  allSelected() {
    return !this.singleSelection && this.model?.length === this.data?.length;
  }

  // Number of currently-selected real options (works for both object and reformatted-id models).
  selectedCount(): number {
    if (this.singleSelection) {
      return this.model != null ? 1 : 0;
    }
    return Array.isArray(this.model) ? this.model.length : 0;
  }

  // True when the button should render a "<n> selected" summary instead of the chip list.
  showCountLabel(): boolean {
    return !!this.showSelectedCount && !this.singleSelection && this.selectedCount() > 0;
  }

  isFirst(option: Option) {
    return option.id == this.data[0].id;
  }

  clickAllEdit() {
    this.isOpened = true;
  }

  getCount(item) {
    return this.data.find(option => option.id === item.id).count;
  }

  // Returns true for rows we injected as group separators.
  isGroupHeader(item: any): boolean {
    return !!item?.[this.groupHeaderFlag];
  }

  // Returns true for rows we injected as "Select all: <label>" actions.
  isSelectAllAction(item: any): boolean {
    return !!item?.[this.selectAllActionFlag];
  }

  // Returns true for the legacy "Check All" row.
  isCheckAllAction(item: any): boolean {
    return !!item?.[this.checkAllActionFlag];
  }

  // Returns true for the legacy "Uncheck All" row.
  isUncheckAllAction(item: any): boolean {
    return !!item?.[this.uncheckAllActionFlag];
  }

  // Returns true for any of our injected pseudo-rows (header / Check / Uncheck / Select-all-by-group).
  isInjectedRow(item: any): boolean {
    return this.isGroupHeader(item)
        || this.isCheckAllAction(item)
        || this.isUncheckAllAction(item)
        || this.isSelectAllAction(item);
  }

  // Selects every (non-injected) option (legacy `Check All`). Emits both `modelChange` and
  // `onSelectAll` so consumers that listen to (onSelectAll) for side-effects (e.g. marking
  // their search filters dirty) react the same way as when the underlying library's built-in
  // toggle fires.
  checkAll(): void {
    if (this.singleSelection || !this.data?.length) {
      return;
    }
    const selectable = this.data.filter(o => !this.isInjectedRow(o));
    let next: any;
    if (this.reformat) {
      next = selectable.map(o => o[this.idProp]);
    } else {
      next = [...selectable];
      this.model = next;
    }
    this.modelChange.emit(next);
    this.onSelectAll.emit(selectable);
    this.updateAllSelectedLabel();
  }

  // Clears the current selection (legacy `Uncheck All`). Also emits `onDeselectAll` so
  // dirty-tracking consumers stay in sync (matches the library's built-in toggle behaviour).
  uncheckAll(): void {
    let next: any;
    if (this.reformat) {
      next = this.singleSelection ? undefined : [];
    } else {
      next = this.singleSelection ? null : [];
      this.model = next;
    }
    this.modelChange.emit(next);
    this.onDeselectAll.emit([]);
    this.updateAllSelectedLabel();
  }

  // Builds the label for a "Select all: X" action row (and for the inline group header row).
  getGroupLabel(value: any): string {
    if (this.groupByTextProvider) {
      return this.groupByTextProvider(value);
    }
    return value == null ? '' : String(value);
  }

  // Selects exactly the options whose `groupBy` property equals `groupValue`, REPLACING the
  // whole current selection. This mirrors the legacy angularjs-dropdown-multiselect
  // `selectCurrentGroup`, which spliced the selection empty before adding the group's items:
  //   - "Select all: Year" selects every year and deselects every group/team (and vice versa);
  //   - if no option of the chosen type exists, the selection is cleared entirely (the other
  //     type is unselected too).
  // Also emits `onSelectAll` so consumers that wire dirty-tracking to (onSelectAll) react.
  selectAllInGroup(groupValue: any): void {
    if (!this.groupBy || !this.data?.length) {
      return;
    }
    const matching = this.data.filter(option => option[this.groupBy] === groupValue);

    let next: any;
    if (this.reformat) {
      const matchingIds = matching.map(option => option[this.idProp]);
      next = this.singleSelection ? matchingIds[0] : matchingIds;
    } else {
      next = this.singleSelection ? (matching[0] ?? null) : [...matching];
      this.model = next;
    }
    this.modelChange.emit(next);
    this.onSelectAll.emit(matching);
    this.updateAllSelectedLabel();
  }

  // Captures clicks on every injected action row inside the dropdown (Check All / Uncheck All /
  // "Select all: X"). The library renders these as disabled <li> rows so its built-in toggle and
  // select-all logic skip them; we intercept the bubbled click event here and dispatch to the
  // matching action based on which row's label was clicked.
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    if (!this.groupBy && !this.enableCheckAllActions) {
      return;
    }
    const target = event.target as HTMLElement;
    const li = target?.closest?.('li.multiselect-item-checkbox') as HTMLElement | null;
    if (!li) {
      return;
    }
    const labelDiv = li.querySelector('div');
    const text = labelDiv?.textContent ?? '';
    if (!text.startsWith(this.selectAllActionMarker)) {
      return;
    }

    if (text === this.buildCheckAllLabel()) {
      event.stopPropagation();
      this.checkAll();
      return;
    }
    if (text === this.buildUncheckAllLabel()) {
      event.stopPropagation();
      this.uncheckAll();
      return;
    }
    for (const groupValue of this.selectByGroups || []) {
      if (text === this.buildSelectAllActionLabel(groupValue)) {
        event.stopPropagation();
        this.selectAllInGroup(groupValue);
        return;
      }
    }
  }

  // Builds the LRM-prefixed labels used by the injected action rows. Kept central so the
  // injection site and the click matcher always agree byte-for-byte.
  private buildCheckAllLabel(): string {
    return this.checkAllMarker + this.translate.instant('SB_Check_All');
  }

  private buildUncheckAllLabel(): string {
    return this.uncheckAllMarker + this.translate.instant('SB_UnCheck_All');
  }

  private buildSelectAllActionLabel(groupValue: any): string {
    return this.selectAllActionMarker + this.translate.instant('SB_Select_All') + ': ' + this.getGroupLabel(groupValue);
  }

  // Sorts `data` by `groupBy` and injects:
  //   1. Legacy "Check All" + "Uncheck All" action rows (always when groupBy is set)
  //   2. "Select all: X" action rows (one per `selectByGroups` value)
  //   3. A disabled header row before the first item of each group
  // Returns `data` unchanged when grouping is not configured.
  private buildGroupedData(data: Options): Options {
    if ((!this.groupBy && !this.enableCheckAllActions) || !data?.length) {
      return data;
    }

    const result: Options = [];

    // Legacy "Check All" / "Uncheck All" rows — injected when grouping is on (the library's
    // built-in single-toggle row is suppressed in that mode) or when a consumer opts in via
    // [enableCheckAllActions].
    result.push({
      [this.idProp]: '__sb_check_all_action__',
      [this.labelProp]: this.buildCheckAllLabel(),
      isDisabled: true,
      [this.checkAllActionFlag]: true
    } as any);
    result.push({
      [this.idProp]: '__sb_uncheck_all_action__',
      [this.labelProp]: this.buildUncheckAllLabel(),
      isDisabled: true,
      [this.uncheckAllActionFlag]: true
    } as any);

    // Without grouping there are no group headers or "Select all: X" rows — just append the
    // options after the Check All / Uncheck All rows.
    if (!this.groupBy) {
      result.push(...data);
      return result;
    }

    if (this.selectByGroups?.length) {
      for (const groupValue of this.selectByGroups) {
        result.push({
          [this.idProp]: `__sb_select_all_action__${groupValue}`,
          [this.labelProp]: this.buildSelectAllActionLabel(groupValue),
          isDisabled: true,
          [this.selectAllActionFlag]: groupValue
        } as any);
      }
    }

    const sorted = [...data].sort((a, b) => {
      const av = a[this.groupBy];
      const bv = b[this.groupBy];
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });

    let lastGroup: any;
    let isFirst = true;
    for (const item of sorted) {
      const group = item[this.groupBy];
      if (isFirst || group !== lastGroup) {
        result.push({
          [this.idProp]: `__sb_group_header__${group}`,
          [this.labelProp]: this.getGroupLabel(group),
          isDisabled: true,
          [this.groupHeaderFlag]: true
        } as any);
        lastGroup = group;
        isFirst = false;
      }
      result.push(item);
    }
    return result;
  }

}
